// Sales page template — hero with inline form, trust strip, USPs, walkthrough, audience, CTA
"use client";

import React from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { ChevronRight, ArrowRight } from "lucide-react";
import Section from "@/components/public/Section";
import { MiniNavBar } from "@/components/public/MiniNavBar";
import { ModernButton } from "@/components/ui/ModernButton";
import { LeadCaptureForm } from "@/components/public/LeadCaptureForm";
import FeatureGrid from "@/components/public/FeatureGrid";
import { PlatformSteps } from "@/components/public/PlatformSteps";
import type { BookDemoData } from "@/data/page-data/book-a-demo";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SalesTemplateProps {
  data: BookDemoData;
}

// ─── Template ─────────────────────────────────────────────────────────────────

export function SalesTemplate({ data }: SalesTemplateProps) {
  const { hero, trustStrip, uspGrid, platformSteps, audienceCards, callAgenda, finalCta, form } = data;

  return (
    <>
      {/* ── Hero — copy left, platform preview right ─────────────────────── */}
      <Section spacing="none" bgColor="#ffffff" extendRight bottomPadding="pb-0">
        <div className="relative z-10 w-full overflow-hidden sm:pl-0 md:pl-6 xl:pl-6">
          {/* MiniNavBar */}
          <div className="px-4 sm:px-6 xl:px-0 xl:mx-[16.67%] sm:mb-6 md:mb-4">
            <MiniNavBar />
          </div>

          {/* Hero grid */}
          <div className="px-4 sm:px-6 xl:px-0 xl:mx-[16.67%] mt-6 sm:mt-8 xl:mt-12 pt-6 pb-14 sm:pb-16 lg:pb-20" id="hero">
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 xl:gap-12 items-start">
              {/* Left — copy */}
              <div className="xl:col-span-6">
                {/* Eyebrow */}
                <motion.p
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-purple-500 mb-4"
                >
                  {hero.eyebrow}
                </motion.p>

                {/* H1 - matches standard service/solution hero sizing */}
                <motion.h1
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.05 }}
                  className="text-2xl sm:text-3xl xl:text-4xl 2xl:text-5xl font-black text-gray-900 leading-tight mb-4 sm:mb-6"
                >
                  {hero.title}
                </motion.h1>

                {/* Subhead */}
                {hero.subhead ? (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="text-base sm:text-lg xl:text-xl font-medium text-gray-700 mb-3 sm:mb-4"
                  >
                    {hero.subhead}
                  </motion.p>
                ) : null}

                {/* Support copy */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.15 }}
                  className="text-sm sm:text-base xl:text-lg leading-relaxed text-gray-600 font-light mb-6 sm:mb-8 max-w-2xl"
                >
                  {hero.supportCopy}
                </motion.p>

                {/* CTAs */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                  className="flex flex-wrap items-center gap-4"
                >
                  <ModernButton href={hero.primaryButton.href} variant="purple" size="lg" showArrow>
                    {hero.primaryButton.text}
                  </ModernButton>
                  <ModernButton href={hero.secondaryButton.href} variant="secondary" size="lg" showArrow>
                    {hero.secondaryButton.text}
                  </ModernButton>
                </motion.div>
              </div>

              {/* Right — platform preview */}
              <div className="xl:col-span-6">
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.15 }}
                >
                  <div className="rounded-3xl border border-slate-200/80 bg-white shadow-[0_22px_55px_-28px_rgba(15,23,42,0.45)] overflow-hidden">
                    <div className="h-1.5 bg-gradient-to-r from-emerald-400 via-blue-500 to-purple-500" />
                    <div className="p-5 sm:p-6 lg:p-7">
                      <div className="flex items-center gap-2 mb-5">
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                        <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                        <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
                        <span className="ml-2 text-xs text-slate-400 font-medium">Replenico Platform Preview</span>
                      </div>

                      <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-5">
                        <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-2">
                          Book a tailored live demo
                        </h3>
                        <p className="text-sm text-slate-600 leading-relaxed mb-4">
                          See exactly how Replenico would run turnovers, standards checks, and replenishment for your portfolio.
                        </p>
                        <Link
                          href="#lead-form"
                          className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:brightness-105"
                          style={{ backgroundColor: "#8c52ff" }}
                        >
                          Book demo
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
        <div className="w-full border-t border-gray-200/80" />
      </Section>

      {/* ── USP grid ──────────────────────────────────────────────────────── */}
      <Section spacing="relaxed" background="transparent">
        <div id="usp-grid">
          <FeatureGrid features={uspGrid} cols={4} showAccent />
        </div>
      </Section>

      {/* ── Lead form (moved higher for conversion) ──────────────────────── */}
      <Section spacing="relaxed" bgColor="#f8fafc">
        <div id="lead-form" className="lg:px-6">
          <div className="mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-satoshi font-bold text-gray-900 mb-2">
                Request your demo call
              </h2>
              <p className="text-sm sm:text-base font-light text-gray-600 leading-relaxed max-w-2xl">
                Tell us about your operation and we will route your request to the right expert.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-3">
              <LeadCaptureForm
                formName={form.name}
                title={form.title}
                intro={form.intro}
                consentText={form.consentText}
                submitButton={form.submitButton}
                successHeadline={form.successHeadline}
                successBody={form.successBody}
                portfolioSizeOptions={form.portfolioSizeOptions}
                propertyTypeOptions={form.propertyTypeOptions}
                helpNeededOptions={form.helpNeededOptions}
                currentSetupOptions={form.currentSetupOptions}
                biggestChallengeOptions={form.biggestChallengeOptions}
                moveTimelineOptions={form.moveTimelineOptions}
                className="h-full"
              />
            </div>

            <div className="lg:col-span-2 space-y-5">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">What happens next</h3>
                <div className="space-y-3">
                  {[
                    "A specialist reviews your portfolio details",
                    "We tailor a live walkthrough around your workflow",
                    "You get clear next steps and implementation options",
                  ].map((item, index) => (
                    <div key={item} className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-600 text-xs font-semibold inline-flex items-center justify-center mt-0.5">
                        {index + 1}
                      </span>
                      <p className="text-sm text-gray-600 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
                <Link
                  href="#hero"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-purple-600 hover:text-purple-700 transition-colors"
                >
                  Back to overview
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>

              <div id="trust-strip" className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
                  Trusted by operators
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {trustStrip.map((item) => (
                    <div key={item.label} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                      <p className="text-base font-bold text-gray-900">{item.value}</p>
                      <p className="text-[11px] text-gray-500 leading-tight">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ── Audience + call agenda (single cohesive block) ───────────────── */}
      <Section
        spacing="relaxed"
        topPadding="pt-10 sm:pt-16 lg:pt-20"
        bottomPadding="pb-10 sm:pb-16 lg:pb-20"
        bgColor="#ffffff"
      >
        <div id="audience-fit" className="lg:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-none sm:rounded-3xl border-0 sm:border border-gray-200 bg-transparent sm:bg-white shadow-none sm:shadow-[0_16px_38px_-30px_rgba(15,23,42,0.3)]"
          >
            <div className="hidden sm:block absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-emerald-400 via-blue-500 to-purple-500" />

            <div className="p-0 sm:p-8 lg:p-10">
              <div className="mb-6 sm:mb-8 lg:mb-10">
                <h2 className="text-2xl sm:text-3xl font-satoshi font-black text-gray-900 mb-2">
                  Built for your operation
                </h2>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl">
                  Replenico works across property types and operator sizes
                </p>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-12 gap-5 sm:gap-8 xl:gap-10 items-start">
                <div className="xl:col-span-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-5">
                    {audienceCards.map((card) => (
                      <Link
                        key={card.title}
                        href={card.href}
                        className="group block h-full rounded-2xl border border-gray-200 bg-gray-50 p-4 sm:p-6 transition-all duration-200 hover:border-gray-300 hover:shadow-md"
                      >
                        <h3 className="text-base font-semibold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
                          {card.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed mb-4">
                          {card.description}
                        </p>
                        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-purple-600 group-hover:gap-2.5 transition-all">
                          Learn more
                          <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>

                <div id="call-agenda" className="xl:col-span-4">
                  <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 shadow-[0_12px_28px_-18px_rgba(15,23,42,0.35)]">
                    <div className="inline-flex items-center rounded-full bg-purple-50 text-purple-600 border border-purple-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider mb-4">
                      Call agenda
                    </div>
                    <h3 className="text-xl sm:text-2xl font-satoshi font-bold text-gray-900 mb-2">
                      {callAgenda.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed mb-5">
                      {callAgenda.description}
                    </p>

                    <div className="space-y-2.5">
                      {callAgenda.bullets.map((bullet, i) => (
                        <div
                          key={bullet}
                          className="flex items-start gap-3 rounded-xl border border-gray-100 bg-gray-50 px-3.5 py-3"
                        >
                          <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold inline-flex items-center justify-center mt-0.5">
                            {i + 1}
                          </span>
                          <span className="text-sm text-gray-700 leading-relaxed">{bullet}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* ── Platform walkthrough (standalone block) ─────────────────────── */}
      <Section
        spacing="relaxed"
        topPadding="pt-10 sm:pt-16 lg:pt-20"
        bottomPadding="pb-10 sm:pb-16 lg:pb-20"
        bgColor="#f8fafc"
      >
        <div id="platform-walkthrough" className="-mx-2 sm:mx-0 lg:px-6">
          <PlatformSteps steps={platformSteps} />
        </div>
      </Section>

      {/* ── Final CTA band — clean, horizontal, left-aligned ────────────── */}
      <Section spacing="relaxed" bgColor="#ffffff">
        <div id="final-cta" className="px-4 lg:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-3xl border border-gray-200 bg-gradient-to-br from-white via-white to-gray-50 shadow-[0_18px_45px_-28px_rgba(15,23,42,0.35)]"
          >
            <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-emerald-400 via-blue-500 to-purple-500" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start p-6 sm:p-8 lg:p-10">
              <div className="lg:col-span-7 text-left">
                <span className="inline-flex items-center rounded-full bg-purple-50 text-purple-600 border border-purple-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider mb-4">
                  Final step
                </span>

                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 mb-3 leading-snug">
                  {finalCta.headline}
                </h2>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-3xl">
                  {finalCta.description}
                </p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                  {[
                    "Tailored to your portfolio setup",
                    "Clear implementation options",
                    "Live workflow walkthrough",
                    "No-obligation consultation",
                  ].map((point) => (
                    <div
                      key={point}
                      className="rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-700"
                    >
                      {point}
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                    Ready when you are
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed mb-5">
                    Book your call and get a focused walkthrough built around your current operation.
                  </p>

                  <Link
                    href={finalCta.button.href}
                    className="inline-flex w-full items-center justify-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-white text-base transition-all duration-200 hover:shadow-md hover:brightness-105"
                    style={{ backgroundColor: "#8c52ff" }}
                  >
                    {finalCta.button.text}
                    <ChevronRight className="w-5 h-5" />
                  </Link>

                  <p className="text-xs text-gray-500 mt-3 text-center">
                    Typical call time: 20 to 30 minutes
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* Mobile sticky CTA */}
      <div className="fixed bottom-4 inset-x-0 z-40 px-4 sm:hidden pointer-events-none">
        <div className="max-w-sm mx-auto pointer-events-auto">
          <Link
            href="#lead-form"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-white font-semibold shadow-lg"
            style={{ backgroundColor: "#8c52ff" }}
          >
            Request your call
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </>
  );
}
